import { gatewayApi as api } from './gatewayApi';

// Types
export interface Product {
  id?: string;
  Name: string;
  Description: string;
  UoMId?: string;
  Value: number;
  PartId?: string;
  Active: boolean;
  
  // Related entities
  UoM?: UoM;
  Part?: Part;
  ProductLots?: ProductLot[];
  
  // Dates
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface ProductLot {
  id?: string;
  ProductLotNo: string;
  Description: string;
  TotalQty: number;
  ProductId?: string;
  
  // Related entities
  Serials?: Serial[];
  PurchaseOrders?: PurchaseOrder[];
  WorkOrders?: WorkOrder[];
  
  // Dates
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface Serial {
  SerialNum: string;
  Id?: string;
  Status?: {
    Id: number;
    Name: string;
  };
}

export interface UoM {
  Id: string;
  Name: string;
  Description?: string;
  Active: boolean;
}

export interface Part {
  Id: string;
  Number: string;
  Description?: string;
  Active: boolean;
}

export interface PurchaseOrder {
  Id: string;
  Number: string;
  Description?: string;
  Active: boolean;
}

export interface WorkOrder {
  Id: string;
  WONum: string;
  Description?: string;
  WOStatusId?: number;
  Active: boolean;
}

export interface SearchProductsRequest {
  pageNum?: number;
  pageSize?: number;
  sort?: string;
  filter?: {
    Active?: boolean;
    Name?: string;
    Description?: string;
    PartId?: string;
    UoMId?: string;
    [key: string]: any;
  };
}

export interface SearchProductsResponse {
  data: Product[];
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateProductRequest {
  Name: string;
  Description: string;
  UoMId?: string;
  Value: number;
  PartId?: string;
  ProductLots?: Partial<ProductLot>[];
  Active?: boolean;
}

// Product API Service
export const productApi = {
  // Product Management
  getAllProducts: (params?: SearchProductsRequest): Promise<SearchProductsResponse> =>
    api.post('/product/search', params || {}),

  getProductById: (id: string): Promise<Product> =>
    api.get(`/product/${id}`),

  createProduct: (data: CreateProductRequest): Promise<Product> =>
    api.post('/product/', data),

  updateProduct: (data: Partial<Product>): Promise<Product> =>
    api.put('/product/', data),

  deleteProduct: (id: string): Promise<void> =>
    api.delete(`/product/${id}`),

  searchProducts: (field: string, value: string): Promise<Product[]> =>
    api.post('/product/search', { filter: { [field]: value } }),

  // Dropdown Data
  getActiveParts: (): Promise<Part[]> =>
    api.post('/part/search', { filter: { Active: true }, page: 1, limit: 100 }),

  getActiveUoMs: (): Promise<UoM[]> =>
    api.post('/uom/search', { filter: { Active: true }, page: 1, limit: 100 }),

  // Serial Management
  getSerialsByWorkOrders: (woIds: string[]): Promise<Serial[]> =>
    api.post('/serial/search', { filter: { WOIds: woIds } }),

  // Work Order Integration
  getWorkOrdersByPOIds: (poIds: string[]): Promise<WorkOrder[]> =>
    api.post('/workorder/search', { filter: { POIds: poIds } }),

  getPurchaseOrdersByPartId: (partId: string): Promise<PurchaseOrder[]> =>
    api.post('/purchaseorder/search', { filter: { PartId: partId } }),

  // Validation utilities
  validateProduct: (product: Partial<Product>): string[] => {
    const errors: string[] = [];
    
    if (!product.Name?.trim()) {
      errors.push('Product Name is required');
    }
    
    if (!product.Description?.trim()) {
      errors.push('Description is required');
    }
    
    if (!product.Value || product.Value <= 0) {
      errors.push('Value must be greater than 0');
    }
    
    return errors;
  },

  validateProductLot: (lot: Partial<ProductLot>): string[] => {
    const errors: string[] = [];
    
    if (!lot.ProductLotNo?.trim()) {
      errors.push('Product Lot Number is required');
    }
    
    if (!lot.Description?.trim()) {
      errors.push('Lot Description is required');
    }
    
    return errors;
  }
}; 